/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import {
  ComponentItemFinderDialogConfig,
  componentItemFinderDialogRoute,
  ComponentItemFinderListItem,
  makeItemFinderId,
} from '@gh/builder-shared';
import { ComponentItemFilterMode } from '@gh/builder-shared-data';
import { getAllNewSelectedEntities } from '@gh/common-shared';
import { emptySelectionModel, SelectionModel, SlicedDataSource } from '@gh/core-data';
import { DialogService, NotificationService, WaitingService } from '@gh/core-ui';
import { filter$, map$ } from '@gh/rx/operators';
import { union } from 'lodash';
import { BulkUpdateBusinessService } from '../../resources/bulk-update-business.service';
import {
  BulkUpdateComponentDelete,
  ComponentBulkUpdateListItem,
} from '../../resources/bulk-update.model';
import { BulkUpdateAbstractComponent } from '../bulk-update-abstract.component';
import { ComponentGridDefinition } from '../component-grid-definition.service';
import { ItemComponentDeleteGridDefinition } from '../item-component-delete-grid-definition.service';

@Component({
  standalone: false,
  selector: 'gh-bulk-update-component-delete',
  templateUrl: 'bulk-update-component-delete.component.html',
  styleUrls: ['bulk-update-component-delete.component.scss'],
  providers: [ComponentGridDefinition, ItemComponentDeleteGridDefinition],
})
export class BulkUpdateComponentDeleteComponent extends BulkUpdateAbstractComponent implements OnInit {

  targetComponentData: SlicedDataSource<ComponentBulkUpdateListItem>;
  deleteItemComponentData: ComponentItemFinderListItem[] = [];
  targetSelectionModel = emptySelectionModel();
  dirty: boolean = false;

  constructor(public componentGridDefinition: ComponentGridDefinition,
              public itemComponentGridDefinition: ItemComponentDeleteGridDefinition,
              private notificationService: NotificationService,
              private waitingService: WaitingService,
              private dialogService: DialogService,
              private businessService: BulkUpdateBusinessService) {
    super();
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.targetComponentData = this.businessService.loadComponentGridDataSource();
  }

  onSave(): void {
    this.businessService.deleteComponentSave(this.getRequestBody()).pipe(
      this.waitingService.operator<any>())
      .subscribe(
        () => this.afterSave(),
        this.notificationService.processAll,
      );
  }

  onAddItemComponent(): void {
    this.dialogService.openWithInput(componentItemFinderDialogRoute, <ComponentItemFinderDialogConfig>{
      multiselection: true,
      disableFilterMode: true,
      filterMode: ComponentItemFilterMode.Items,
      preselected: this.deleteItemComponentData.map((item) => makeItemFinderId(item.id)),
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe(
        (componentItem: ComponentItemFinderListItem[]) => {
          this.deleteItemComponentData = union(this.deleteItemComponentData, componentItem);
          this.resetDirty(true);
        },
      );
  }

  onTargetSelectionChanged(model: SelectionModel): void {
    this.resetDirty(true);
    this.targetSelectionModel = model;
  }

  onRemoveItem(itemComponent: ComponentItemFinderListItem): void {
    this.deleteItemComponentData = this.deleteItemComponentData.filter((item) => item !== itemComponent);
  }

  protected getRequestBody(): BulkUpdateComponentDelete {
    return {
      targetComponents: this.targetSelectionModel,
      items: this.deleteItemComponentData.map((item) => item.id),
    };
  }

  protected afterSave(): void {
    this.notificationService.success('msg_bulk_update_initiated_successfully');
    this.deleteItemComponentData = [];
    this.targetSelectionModel = emptySelectionModel();
    this.resetDirty(false);
  }
}
